import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { cx, focusablesIn, isApplePlatform, isFilled, PortalThemeContext } from '@/shared/lib'
import { Icon } from '@/shared/ui/Icon'
import { IconButton } from '@/shared/ui/IconButton'
import { Surface } from '@/shared/ui/Surface'
import s from './Modal.module.css'

/**
 * Ступени предельной ширины: `xs` 420 — подтверждение, `sm` 520 — короткая форма,
 * `md` 620 — форма со списком, `lg` 720 — два столбца, `xl` 820 — рабочий список.
 * Шкала неконтрола: подряд от `xs`, к `--control-h-*` не привязана.
 */
export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

/** `alertdialog` — необратимое подтверждение: AT читает содержимое немедленно. */
export type ModalRole = 'dialog' | 'alertdialog'

type ModalOwnProps = {
  /** Открыта ли модалка. Закрытая не рендерит ничего — ни портала, ни оверлея. */
  open: boolean
  /** Просьба закрыться: крестик, Esc, клик по фону. Открывает модалку потребитель. */
  onClose: () => void
  size?: ModalSize
  role?: ModalRole
  /** Надстрочник над заголовком: «ЗАПРОС МЕНЕДЖЕРА · №14-882», артикул, «Шаг 1 из 2». */
  eyebrow?: ReactNode
  /** Пояснение под заголовком. Уезжает в `aria-describedby`. */
  description?: ReactNode
  /** Слот перед заголовком: плитка тона у подтверждения, миниатюра товара. */
  icon?: ReactNode
  /** Слот действий внизу. Вместе с телом не прокручивается. */
  footer?: ReactNode
  /** Тело. Необязательно: у подтверждения весь смысл в заголовке и описании. */
  children?: ReactNode
  /** Закрывают ли Esc и клик по фону. Крестик работает всегда. */
  dismissible?: boolean
  /** Куда встаёт фокус при открытии. Не задан — фокус берёт сама карточка. */
  initialFocusRef?: RefObject<HTMLElement | null>
  /** Указывает на карточку: измерение, прокрутка к элементу. */
  ref?: Ref<HTMLDivElement>
  /* role задаётся союзом ModalRole, title занят нативным тултипом и путался бы с заголовком */
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'role' | 'title'>

/** Модалка без доступного имени не собирается: либо видимый заголовок, либо `label`. */
type ModalNameProps =
  { heading: ReactNode; label?: string } | { heading?: undefined; label: string }

/** Крестик без доступного имени тоже не собирается. */
type ModalCloseProps =
  { closeButton?: true; closeLabel: string } | { closeButton: false; closeLabel?: never }

export type ModalProps = ModalOwnProps & ModalNameProps & ModalCloseProps

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const SIZE_CLASS: Record<ModalSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
  md: s.md,
  lg: s.lg,
  xl: s.xl,
}

/*
 * Табстопы модалки: общий обход из shared/lib плюс одна надстройка — у группы радиокнопок
 * табстоп ровно один (выбранная, а при пустой группе — первая), а querySelectorAll возвращает
 * все, и «последним» узлом модалки оказывался недостижимый. Формы бывают только здесь,
 * поэтому проверка живёт здесь же, а не в общем обходе.
 */
function tabbablesIn(root: HTMLElement): HTMLElement[] {
  const nodes = focusablesIn(root)

  return nodes.filter((node) => {
    if (!(node instanceof HTMLInputElement) || node.type !== 'radio') return true

    /*
     * Без name группы не существует: браузер такую кнопку в набор не собирает, табстопом
     * она остаётся сама по себе. Искать её по `[name=""]` бессмысленно — селектор не совпадёт
     * ни с чем, и узел молча выпал бы из ловушки (Radio name не требует).
     */
    if (node.name === '') return true

    const group = Array.from(
      root.querySelectorAll<HTMLInputElement>(
        `input[type="radio"][name="${CSS.escape(node.name)}"]`,
      ),
    )
    // Табстоп группы ровно один и уникален внутри неё: отдельный набор виденных имён не нужен
    const stop = group.find((item) => item.checked) ?? group[0]
    return stop === node
  })
}

/*
 * Стек открытых модалок. Модульный, а не контекстом: контекст не пересекает порталы
 * соседних поддеревьев, а вложенная модалка вполне может открыться из другой ветки React.
 * Нужен ровно для одного случая — Esc при потерянном фокусе обрабатывает только верхняя.
 */
const openStack: string[] = []

// openStack[len - 1], а не .at(-1): Array.prototype.at — ES2022, Safari 15.4+,
// а планка проекта 15.4. esbuild транспилирует синтаксис, но не методы прототипа.
const isTopmost = (id: string): boolean => openStack[openStack.length - 1] === id

/*
 * Блокировка прокрутки фона. Счётчик, а не флаг: закрытие подтверждения поверх формы
 * не должно разблокировать страницу под нижней модалкой.
 */
type SavedBodyStyle = {
  overflow: string
  paddingRight: string
  position: string
  top: string
  left: string
  right: string
}

let lockCount = 0
let savedBodyStyle: SavedBodyStyle | null = null
let savedScrollY = 0
let lockedAsIos = false

/**
 * iOS: `overflow: hidden` на body страницу не держит — она продолжает тянуться под оверлеем,
 * а после закрытия остаётся в другом месте. Единственное лечение без зависимостей —
 * перевести body в `position: fixed` со сдвигом на текущую прокрутку.
 * На десктопном маке и на Windows приём не нужен, а перерисовку всей страницы стоит.
 */
const needsIosScrollFix = (): boolean =>
  isApplePlatform() && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0

function lockBodyScroll(): void {
  lockCount += 1
  if (lockCount > 1) return

  const { body } = document

  /*
   * Поповер рендерится порталом в body, а не внутрь карточки, поэтому переменную слоя
   * нельзя поставить на оверлей — она туда не наследуется. Пока модалка открыта, поднимаем
   * слой поповеров на самом body: иначе Select и фильтр внутри модальной формы окажутся
   * под её же скримом. Снимается вместе с блокировкой прокрутки.
   */
  body.style.setProperty('--popover-z', 'calc(var(--z-modal) + 1)')
  savedBodyStyle = {
    overflow: body.style.overflow,
    paddingRight: body.style.paddingRight,
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
  }
  savedScrollY = window.scrollY
  lockedAsIos = needsIosScrollFix()

  // Полоса прокрутки исчезает вместе со скроллом: без компенсации страница под скримом
  // прыгает вправо на её ширину
  const gap = window.innerWidth - document.documentElement.clientWidth
  if (gap > 0) {
    const current = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0
    body.style.paddingRight = `${current + gap}px`
  }

  body.style.overflow = 'hidden'
  if (lockedAsIos) {
    body.style.position = 'fixed'
    body.style.top = `${-savedScrollY}px`
    // Без left/right у fixed ширина считается по содержимому, и страница под скримом сжимается вбок
    body.style.left = '0'
    body.style.right = '0'
  }
}

function unlockBodyScroll(): void {
  lockCount = Math.max(0, lockCount - 1)
  if (lockCount > 0 || savedBodyStyle === null) return

  const { body } = document
  body.style.removeProperty('--popover-z')
  body.style.overflow = savedBodyStyle.overflow
  body.style.paddingRight = savedBodyStyle.paddingRight
  body.style.position = savedBodyStyle.position
  body.style.left = savedBodyStyle.left
  body.style.right = savedBodyStyle.right
  body.style.top = savedBodyStyle.top
  savedBodyStyle = null

  // Прокрутка возвращается в том же кадре, что и снятие position: fixed — иначе страница мигает
  if (lockedAsIos) window.scrollTo(0, savedScrollY)
  lockedAsIos = false
}

/**
 * Модальный слой: затемняет страницу, забирает фокус и требует решения, не уводя со страницы.
 * Быстрый просмотр товара, утверждение скидки, приглашение сотрудника, вставка списка артикулов,
 * подтверждение удаления позиции.
 *
 * Портал в `body` и `position: fixed` — как у `Popover` и по той же причине: `backdrop-filter`
 * у `Surface` создаёт containing block, и `fixed` внутри стеклянной панели перестаёт быть
 * фиксированным относительно окна.
 *
 * Нативный `<dialog>` не используется: `showModal()` появился в Safari 15.4, планка — 14.1.
 * Ловушка фокуса поэтому своя, циклом по Tab внутри карточки, без `inert` на фоне: `inert`
 * погасил бы и панели `Popover`, которые живут порталом в том же `body`.
 *
 * Контракт: docs/ui-kit/contracts/modal.md
 */
export function Modal({
  open,
  onClose,
  size = 'md',
  role = 'dialog',
  heading,
  label,
  eyebrow,
  description,
  icon,
  footer,
  children,
  closeButton = true,
  closeLabel,
  dismissible = true,
  initialFocusRef,
  className,
  id,
  onKeyDown,
  ref,
  ...rest
}: ModalProps) {
  // Хук — до ранних return: тема экрана, из которого открыт портал (вход — тёмный)
  const portalTheme = useContext(PortalThemeContext)

  const generatedId = useId()
  const cardId = id ?? generatedId
  const headingId = `${cardId}-heading`
  const descriptionId = `${cardId}-description`

  const cardRef = useRef<HTMLDivElement | null>(null)

  const hasHeading = isFilled(heading)
  const hasDescription = isFilled(description)
  const hasHeader = hasHeading || hasDescription || isFilled(eyebrow) || isFilled(icon)
  const hasFooter = isFilled(footer)

  /** Карточка нужна и снаружи (потребителю), и внутри (фокус, ловушка): ref раздваивается здесь. */
  const attachCard = useCallback(
    (node: HTMLElement | null) => {
      // Surface типизирует ref как HTMLElement; сужение проверкой, а не приведением через as
      const card = node instanceof HTMLDivElement ? node : null

      cardRef.current = card
      // ref-колбэк потребителя в React 19 вправе вернуть cleanup — его нельзя терять
      if (typeof ref === 'function') return ref(card)
      if (ref) ref.current = card
      return undefined
    },
    [ref],
  )

  // Стек открытых: нужен Esc при потерянном фокусе, чтобы сработала только верхняя модалка
  useEffect(() => {
    if (!open) return

    openStack.push(cardId)
    return () => {
      const index = openStack.indexOf(cardId)
      if (index !== -1) openStack.splice(index, 1)
    }
  }, [open, cardId])

  /*
   * Возврат фокуса тому, кто открыл. Слой раскладки, а не пассивный эффект: чистка обязана
   * успеть до удаления узлов из DOM, иначе document.activeElement уже сброшен и «фокус был
   * внутри» не отличить от «пользователь ушёл сам». Эффект объявлен раньше начального фокуса —
   * порядок объявления и есть порядок выполнения.
   */
  useLayoutEffect(() => {
    if (!open) return

    const active = document.activeElement
    // body в открыватели не годится: вернуть ему фокус — то же самое, что не возвращать никому
    const opener = active instanceof HTMLElement && active !== document.body ? active : null

    return () => {
      const card = cardRef.current
      const active = document.activeElement
      const inside = card !== null && active instanceof Node && card.contains(active)
      // Карточка уже снята из DOM вместе с фокусом — браузер сбросил его в body
      const lost = active === null || active === document.body

      // Открыватель мог исчезнуть вместе со строкой таблицы, из которой модалку позвали
      if ((inside || lost) && opener?.isConnected) opener.focus({ preventScroll: true })
    }
  }, [open])

  // Начальный фокус: карточка, а не первый элемент — иначе скринридер начнёт диалог с «Закрыть»
  useLayoutEffect(() => {
    if (!open) return

    const target = initialFocusRef?.current ?? cardRef.current
    target?.focus({ preventScroll: true })
  }, [open, initialFocusRef])

  useEffect(() => {
    if (!open) return

    lockBodyScroll()
    return unlockBodyScroll
  }, [open])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const card = cardRef.current
      if (!card) return

      const target = event.target
      const inside = target instanceof Node && card.contains(target)
      /*
       * Фокус потерян (возврат из адресной строки): активен body, и Tab увёл бы в фон
       * под оверлеем. Заводим его обратно в карточку. Проверка узкая — порталам не мешает.
       */
      const nowhere = target === document.body || target === document.documentElement

      if (event.key === 'Tab' && nowhere && isTopmost(cardId)) {
        event.preventDefault()
        const [first] = tabbablesIn(card)
        ;(first ?? card).focus({ preventScroll: true })
        return
      }

      if (event.key !== 'Escape' || event.defaultPrevented || !dismissible) return

      /*
       * Esc обрабатывает та модалка, внутри которой лежит цель события. Это разводит три случая:
       * открытый Popover (портал вне карточки) закрывает себя, а не модалку; вложенная модалка
       * закрывается сама; при потерянном фокусе отвечает верхняя модалка стека.
       */
      if (!inside && !(nowhere && isTopmost(cardId))) return

      onClose()
    }

    // Всплытие, а не перехват: содержимое вправе обработать Esc раньше и погасить событие
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, dismissible, onClose, cardId])

  /** Ловушка фокуса: Tab по кругу внутри карточки. Фон гасить нечем — см. контракт, п. 3. */
  const handleCardKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event)

    const card = cardRef.current
    if (event.key !== 'Tab' || event.defaultPrevented || !card) return

    const focusables = tabbablesIn(card)
    const active = document.activeElement

    if (focusables.length === 0) {
      // Фокусировать внутри нечего: Tab не имеет права увести в фон под оверлеем
      event.preventDefault()
      card.focus({ preventScroll: true })
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    // Вперёд с последнего — на первый; назад с первого (и с самой карточки) — на последний
    if (!event.shiftKey && active === last && first) {
      event.preventDefault()
      first.focus()
      return
    }

    if (event.shiftKey && (active === first || active === card) && last) {
      event.preventDefault()
      last.focus()
    }
  }

  /*
   * Нажатие, а не клик: click приходит только после отпускания кнопки и срабатывает на общем
   * предке, если выделение текста началось в карточке, а закончилось на скриме. Тот же довод,
   * что у «клика вне» в Popover. Проверка currentTarget отсекает всё, что пришло из карточки.
   */
  const handleOverlayPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dismissible || event.target !== event.currentTarget) return
    onClose()
  }

  if (!open) return null

  return createPortal(
    <div
      className={s.overlay}
      // Тема экрана, из которого открыто окно (вход — тёмный): и подложка, и карточка
      data-theme={portalTheme ?? undefined}
      onPointerDown={handleOverlayPointerDown}
    >
      <Surface
        {...rest}
        ref={attachCard}
        id={cardId}
        className={cx(s.card, SIZE_CLASS[size], className)}
        variant="overlay"
        radius="panel"
        elevation="modal"
        padding="none"
        // Под 96-процентной заливкой размытия не видно, а стоит оно дороже всего на экране
        blur={false}
        role={role}
        aria-modal="true"
        aria-label={label}
        aria-labelledby={label === undefined && hasHeading ? headingId : undefined}
        aria-describedby={hasDescription ? descriptionId : undefined}
        tabIndex={-1}
        data-size={size}
        onKeyDown={handleCardKeyDown}
      >
        {closeButton && closeLabel !== undefined && (
          <IconButton
            className={s.close}
            icon={<Icon name="close" size="sm" />}
            label={closeLabel}
            variant="subtle"
            size="sm"
            onClick={onClose}
          />
        )}

        {hasHeader && (
          <div className={cx(s.header, closeButton && s.underClose)}>
            {isFilled(icon) && <span className={s.icon}>{icon}</span>}

            <div className={s.headerText}>
              {isFilled(eyebrow) && <span className={s.eyebrow}>{eyebrow}</span>}
              {hasHeading && (
                <h2 id={headingId} className={s.heading}>
                  {heading}
                </h2>
              )}
              {hasDescription && (
                <p id={descriptionId} className={s.description}>
                  {description}
                </p>
              )}
            </div>
          </div>
        )}

        {isFilled(children) && (
          <div
            className={cx(
              s.body,
              !hasHeader && s.bodyFlush,
              // Шапки нет — под крестик уходит верх тела: иначе текст лезет под кнопку
              !hasHeader && closeButton && s.underClose,
              !hasFooter && s.bodyTail,
            )}
          >
            {children}
          </div>
        )}

        {hasFooter && <div className={s.footer}>{footer}</div>}
      </Surface>
    </div>,
    document.body,
  )
}
